// Extension is explicit: this module is loaded directly by the Node test runner.
import { isNonSubject } from "./recordType.js";

export function normalizeName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[–—-]/g, " ")
    .replace(/[()]/g, "")
    .replace(/[.,:!?']/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function toArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value == null) return [];
  return [value].filter(Boolean);
}

function normalizeRelationships(relationships) {
  if (!relationships) return [];

  if (Array.isArray(relationships)) {
    return relationships
      .flatMap((rel) => {
        if (!rel) return [];

        if (typeof rel === "string") {
          return [{ type: "related", to: rel }];
        }

        if (typeof rel === "object") {
          const target = rel.to || rel.id || rel.personId || rel.ref;
          if (!target) return [];

          return [
            {
              type: rel.type || "related",
              to: target,
            },
          ];
        }

        return [];
      })
      .filter((rel) => rel?.to);
  }

  if (typeof relationships === "object") {
    return Object.entries(relationships)
      .flatMap(([type, value]) =>
        toArray(value).flatMap((entry) => {
          if (!entry) return [];

          if (typeof entry === "string") {
            return [{ type, to: entry }];
          }

          if (typeof entry === "object") {
            const target =
              entry.to || entry.id || entry.personId || entry.ref;
            if (!target) return [];

            return [
              {
                type: entry.type || type || "related",
                to: target,
              },
            ];
          }

          return [];
        })
      )
      .filter((rel) => rel?.to);
  }

  return [];
}

export function createPeopleSearch(peopleData = []) {
  /*
   * Taxonomy rows are not study subjects, so they stay out of the corpus
   * entirely -- otherwise searching "civil" returned the "Internal" conflict
   * category as though it were a person.
   */
  const PEOPLE = (peopleData || []).filter(
    (p) => p?.name && !isNonSubject(p)
  );

  const NORMALIZED_PEOPLE = PEOPLE.map((person) => {
    const normalizedId = normalizeName(person.id);
    const normalizedName = normalizeName(person.name);
    const normalizedTitle = normalizeName(person.title);
    const normalizedEra = normalizeName(person.era);
    const normalizedAliases = toArray(person.aliases).map(normalizeName);
    const normalizedRelated = toArray(person.related).map(normalizeName);
    const normalizedRelationshipTargets = normalizeRelationships(
      person.relationships
    ).map((rel) => normalizeName(rel.to));

    return {
      ...person,
      _normalizedId: normalizedId,
      _normalizedName: normalizedName,
      _normalizedTitle: normalizedTitle,
      _normalizedEra: normalizedEra,
      _normalizedAliases: normalizedAliases,
      _normalizedRelated: normalizedRelated,
      _normalizedRelationshipTargets: normalizedRelationshipTargets,
      _searchBlob: [
        normalizedId,
        normalizedName,
        normalizedTitle,
        normalizedEra,
        ...normalizedAliases,
        ...normalizedRelated,
        ...normalizedRelationshipTargets,
      ]
        .filter(Boolean)
        .join(" | "),
    };
  });

  const peopleById = Object.create(null);
  const peopleLookup = Object.create(null);
  const duplicateNameCountMap = Object.create(null);
  /*
   * Keyed "name|title", so a suggestion subtitle can tell whether the title
   * already separates two people who share a name. See getSuggestionSubtitle.
   */
  const duplicateTitleCountMap = Object.create(null);
  const exactNameMap = Object.create(null);
  const exactAliasMap = Object.create(null);

  /*
   * Ids are claimed in their own pass first.
   *
   * peopleLookup is a single namespace holding ids, names and aliases, so a
   * record whose NAME matches an earlier record's ID used to silently steal
   * that key -- resolvePersonRef("simeon") returned Simeon of Luke 2 rather
   * than Jacob's son. Relationship arrays store ids, so an id must always win.
   */
  const idKeys = new Set();

  for (let i = 0; i < NORMALIZED_PEOPLE.length; i += 1) {
    const person = NORMALIZED_PEOPLE[i];

    if (person.id) {
      peopleById[person.id] = person;
      peopleLookup[person.id] = person;
      idKeys.add(person.id);
    }
    if (person._normalizedId) {
      peopleById[person._normalizedId] = person;
      peopleLookup[person._normalizedId] = person;
      idKeys.add(person._normalizedId);
    }
  }

  for (let i = 0; i < NORMALIZED_PEOPLE.length; i += 1) {
    const person = NORMALIZED_PEOPLE[i];

    if (person._normalizedName) {
      if (!idKeys.has(person._normalizedName)) {
        peopleLookup[person._normalizedName] = person;
      }
      duplicateNameCountMap[person._normalizedName] =
        (duplicateNameCountMap[person._normalizedName] || 0) + 1;

      const titleKey = `${person._normalizedName}|${normalizeName(
        person.title || person.era || "Person"
      )}`;
      duplicateTitleCountMap[titleKey] =
        (duplicateTitleCountMap[titleKey] || 0) + 1;

      if (!exactNameMap[person._normalizedName]) {
        exactNameMap[person._normalizedName] = [];
      }
      exactNameMap[person._normalizedName].push(person);
    }

    for (let j = 0; j < person._normalizedAliases.length; j += 1) {
      const normalizedAlias = person._normalizedAliases[j];
      if (!normalizedAlias) continue;

      if (!idKeys.has(normalizedAlias)) {
        peopleLookup[normalizedAlias] = person;
      }

      if (!exactAliasMap[normalizedAlias]) {
        exactAliasMap[normalizedAlias] = [];
      }
      exactAliasMap[normalizedAlias].push(person);
    }
  }

  function getNormalizedAliases(person) {
    return person?._normalizedAliases || [];
  }

  function resolvePersonRef(ref) {
    if (!ref) return null;

    const raw = String(ref).trim();
    const cleaned = normalizeName(raw);

    return peopleLookup[raw] || peopleLookup[cleaned] || null;
  }

  function getDuplicateNameCount(person) {
    if (!person?._normalizedName) return 0;
    return duplicateNameCountMap[person._normalizedName] || 0;
  }

  /*
   * A human-readable detail that separates two people sharing a name AND a
   * title. Prefers the era, falling back to the first Scripture reference.
   */
  function getDisambiguator(person, base) {
    const normalizedBase = normalizeName(base);

    const candidates = [
      person.era,
      Array.isArray(person.scripture) ? person.scripture[0] : person.scripture,
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const candidate = candidates[i];

      /*
       * Skip anything the title already says, so we never render
       * "Son of Saul • Son of Saul".
       */
      if (candidate && !normalizedBase.includes(normalizeName(candidate))) {
        return candidate;
      }
    }

    return "";
  }

  /*
   * Builds the line shown under a person's name in search results.
   *
   * The title on its own separates all but one of the duplicated names in the
   * index, so a qualifier is only added when the titles collide too. This used
   * to append person.id, which put raw slugs such as "ahinoam-wife-of-david"
   * in front of readers.
   */
  function getSuggestionSubtitle(person) {
    const base = person.title || person.era || "Person";

    if (getDuplicateNameCount(person) < 2) return base;

    const sharesTitle = (duplicateTitleCountMap[
      `${person._normalizedName}|${normalizeName(base)}`
    ] || 0) > 1;

    if (!sharesTitle) return base;

    const qualifier = getDisambiguator(person, base);

    return qualifier ? `${base} • ${qualifier}` : base;
  }

  function scorePersonMatch(person, cleaned) {
    const personId = person._normalizedId;
    const name = person._normalizedName;
    const title = person._normalizedTitle;
    const era = person._normalizedEra;
    const aliases = person._normalizedAliases;

    let score = 0;

    if (personId === cleaned) score += 300;
    else if (personId.startsWith(cleaned)) score += 150;
    else if (personId.includes(cleaned)) score += 95;

    if (name === cleaned) score += 180;
    else if (name.startsWith(cleaned)) score += 130;
    else if (name.includes(cleaned)) score += 80;

    let aliasScore = 0;
    for (let i = 0; i < aliases.length; i += 1) {
      const alias = aliases[i];

      if (alias === cleaned) {
        aliasScore = Math.max(aliasScore, 170);
      } else if (alias.startsWith(cleaned)) {
        aliasScore = Math.max(aliasScore, 115);
      } else if (alias.includes(cleaned)) {
        aliasScore = Math.max(aliasScore, 70);
      }
    }
    score += aliasScore;

    if (title.includes(cleaned)) score += 25;
    if (era.includes(cleaned)) score += 20;

    return score;
  }

  function getMatchDetails(person, cleaned) {
    const personId = person._normalizedId;
    const name = person._normalizedName;
    const title = person._normalizedTitle;
    const era = person._normalizedEra;
    const aliases = getNormalizedAliases(person);

    if (personId === cleaned) {
      return {
        matchedOn: "id-exact",
        matchedValue: person.id,
      };
    }

    if (name === cleaned) {
      return {
        matchedOn: "name-exact",
        matchedValue: person.name,
      };
    }

    const exactAliasIndex = aliases.indexOf(cleaned);
    if (exactAliasIndex !== -1) {
      return {
        matchedOn: "alias-exact",
        matchedValue: toArray(person.aliases)[exactAliasIndex] || null,
      };
    }

    if (personId.startsWith(cleaned)) {
      return {
        matchedOn: "id-prefix",
        matchedValue: person.id,
      };
    }

    if (name.startsWith(cleaned)) {
      return {
        matchedOn: "name-prefix",
        matchedValue: person.name,
      };
    }

    for (let i = 0; i < aliases.length; i += 1) {
      if (aliases[i].startsWith(cleaned)) {
        return {
          matchedOn: "alias-prefix",
          matchedValue: toArray(person.aliases)[i] || null,
        };
      }
    }

    if (personId.includes(cleaned)) {
      return {
        matchedOn: "id-partial",
        matchedValue: person.id,
      };
    }

    if (name.includes(cleaned)) {
      return {
        matchedOn: "name-partial",
        matchedValue: person.name,
      };
    }

    for (let i = 0; i < aliases.length; i += 1) {
      if (aliases[i].includes(cleaned)) {
        return {
          matchedOn: "alias-partial",
          matchedValue: toArray(person.aliases)[i] || null,
        };
      }
    }

    if (title.includes(cleaned)) {
      return {
        matchedOn: "title-partial",
        matchedValue: person.title,
      };
    }

    if (era.includes(cleaned)) {
      return {
        matchedOn: "era-partial",
        matchedValue: person.era,
      };
    }

    return {
      matchedOn: "unknown",
      matchedValue: null,
    };
  }

  function compareRankedMatches(a, b) {
    if (b.score !== a.score) return b.score - a.score;
    if (b.duplicateCount !== a.duplicateCount) {
      return b.duplicateCount - a.duplicateCount;
    }

    return (
      a.person.name.localeCompare(b.person.name) ||
      a.person.id.localeCompare(b.person.id)
    );
  }

  function insertTopMatch(topMatches, entry, limit) {
    let inserted = false;

    for (let i = 0; i < topMatches.length; i += 1) {
      if (compareRankedMatches(entry, topMatches[i]) < 0) {
        topMatches.splice(i, 0, entry);
        inserted = true;
        break;
      }
    }

    if (!inserted) {
      topMatches.push(entry);
    }

    if (topMatches.length > limit) {
      topMatches.length = limit;
    }
  }

  function getRankedPersonMatches(query, limit = Infinity) {
    const cleaned = normalizeName(query);
    if (!cleaned) return [];

    if (!Number.isFinite(limit) || limit <= 0) {
      const matches = [];

      for (let i = 0; i < NORMALIZED_PEOPLE.length; i += 1) {
        const person = NORMALIZED_PEOPLE[i];

        if (!person._searchBlob.includes(cleaned)) continue;

        const score = scorePersonMatch(person, cleaned);

        if (score > 0) {
          matches.push({
            person,
            score,
            duplicateCount: getDuplicateNameCount(person),
          });
        }
      }

      matches.sort(compareRankedMatches);

      return matches.map(({ person, score }) => ({
        ...person,
        score,
      }));
    }

    const topMatches = [];

    for (let i = 0; i < NORMALIZED_PEOPLE.length; i += 1) {
      const person = NORMALIZED_PEOPLE[i];

      if (!person._searchBlob.includes(cleaned)) continue;

      const score = scorePersonMatch(person, cleaned);
      if (score <= 0) continue;

      const entry = {
        person,
        score,
        duplicateCount: getDuplicateNameCount(person),
      };

      if (topMatches.length < limit) {
        insertTopMatch(topMatches, entry, limit);
        continue;
      }

      const worst = topMatches[topMatches.length - 1];
      if (compareRankedMatches(entry, worst) > 0) {
        continue;
      }

      insertTopMatch(topMatches, entry, limit);
    }

    return topMatches.map(({ person, score }) => ({
      ...person,
      score,
    }));
  }

  function addUniquePerson(results, seen, person, excludeId = null) {
    if (!person?.id) return;
    if (excludeId && person.id === excludeId) return;
    if (seen.has(person.id)) return;

    seen.add(person.id);
    results.push(person);
  }

  function matchesTargetRef(ref, targetId, targetName, targetAliases = []) {
    if (!ref) return false;

    const resolved = resolvePersonRef(ref);
    if (resolved?.id === targetId) return true;

    const cleanedRef = normalizeName(ref);

    if (cleanedRef === normalizeName(targetId)) return true;
    if (cleanedRef === normalizeName(targetName)) return true;

    for (let i = 0; i < targetAliases.length; i += 1) {
      if (normalizeName(targetAliases[i]) === cleanedRef) return true;
    }

    return false;
  }

  function getPersonById(personId) {
    if (!personId) return null;

    const raw = String(personId).trim();
    const cleaned = normalizeName(raw);

    return peopleById[raw] || peopleById[cleaned] || null;
  }

  function getPeopleSuggestions(query, limit = 8) {
    const cleaned = normalizeName(query);
    const ranked = getRankedPersonMatches(query, limit);

    return ranked.map((person) => {
      const details = getMatchDetails(person, cleaned);

      return {
        type: "person",
        title: person.name,
        subtitle: getSuggestionSubtitle(person),
        route: `/people/${person.id}`,
        person,
        score: person.score,
        matchedOn: details.matchedOn,
        matchedValue: details.matchedValue,
      };
    });
  }

  function findPersonMatchWithScore(query) {
    const raw = String(query || "").trim();
    if (!raw) return null;

    const cleaned = normalizeName(raw);

    const exactIdMatch = peopleById[raw] || peopleById[cleaned];
    if (exactIdMatch) {
      return {
        person: exactIdMatch,
        score: scorePersonMatch(exactIdMatch, cleaned),
        matchedOn: "id-exact",
        matchedValue: exactIdMatch.id,
      };
    }

    const aliasMatches = exactAliasMap[cleaned] || [];
    if (aliasMatches.length === 1) {
      const person = aliasMatches[0];
      const aliasIndex = person._normalizedAliases.indexOf(cleaned);

      return {
        person,
        score: scorePersonMatch(person, cleaned),
        matchedOn: "alias-exact",
        matchedValue: toArray(person.aliases)[aliasIndex] || null,
      };
    }

    const exactNameMatches = exactNameMap[cleaned] || [];
    if (exactNameMatches.length === 1) {
      const person = exactNameMatches[0];
      return {
        person,
        score: scorePersonMatch(person, cleaned),
        matchedOn: "name-exact",
        matchedValue: person.name,
      };
    }

    const ranked = getRankedPersonMatches(query);
    if (!ranked.length) return null;

    if (ranked.length === 1) {
      const person = ranked[0];
      const details = getMatchDetails(person, cleaned);

      return {
        person,
        score: person.score,
        matchedOn: details.matchedOn,
        matchedValue: details.matchedValue,
      };
    }

    const top = ranked[0];
    const second = ranked[1];

    const exactNameCollision =
      top._normalizedName === cleaned && second._normalizedName === cleaned;

    if (exactNameCollision) {
      return {
        person: null,
        score: top.score,
        matchedOn: "ambiguous-exact-name",
        matchedValue: cleaned,
        alternatives: ranked.slice(0, 5).map((person) => ({
          id: person.id,
          name: person.name,
          title: person.title || null,
          score: person.score,
        })),
      };
    }

    if (top.score >= second.score + 60) {
      const details = getMatchDetails(top, cleaned);

      return {
        person: top,
        score: top.score,
        matchedOn: details.matchedOn,
        matchedValue: details.matchedValue,
        runnerUp: {
          id: second.id,
          name: second.name,
          title: second.title || null,
          score: second.score,
        },
      };
    }

    return {
      person: null,
      score: top.score,
      matchedOn: "ambiguous-close-score",
      matchedValue: cleaned,
      alternatives: ranked.slice(0, 5).map((person) => ({
        id: person.id,
        name: person.name,
        title: person.title || null,
        score: person.score,
      })),
    };
  }

  function findPersonMatch(query) {
    const raw = String(query || "").trim();
    if (!raw) return null;

    const cleaned = normalizeName(raw);

    const exactIdMatch = peopleById[raw] || peopleById[cleaned];
    if (exactIdMatch) return exactIdMatch;

    const aliasMatches = exactAliasMap[cleaned] || [];
    if (aliasMatches.length === 1) return aliasMatches[0];

    const exactNameMatches = exactNameMap[cleaned] || [];
    if (exactNameMatches.length === 1) return exactNameMatches[0];

    return null;
  }

  function getRelatedPeopleForPerson(personId) {
    const person = getPersonById(personId);
    if (!person) return [];

    const targetId = person.id;
    const targetName = person.name;
    const targetAliases = toArray(person.aliases);

    const results = [];
    const seen = new Set();

    toArray(person.related).forEach((ref) => {
      const match = resolvePersonRef(ref);
      addUniquePerson(results, seen, match, targetId);
    });

    normalizeRelationships(person.relationships).forEach((rel) => {
      const match = resolvePersonRef(rel?.to);
      addUniquePerson(results, seen, match, targetId);
    });

    for (let i = 0; i < NORMALIZED_PEOPLE.length; i += 1) {
      const other = NORMALIZED_PEOPLE[i];
      if (!other?.id || other.id === targetId) continue;

      const hasReverseRelated = toArray(other.related).some((ref) =>
        matchesTargetRef(ref, targetId, targetName, targetAliases)
      );

      if (hasReverseRelated) {
        addUniquePerson(results, seen, other, targetId);
      }
    }

    for (let i = 0; i < NORMALIZED_PEOPLE.length; i += 1) {
      const other = NORMALIZED_PEOPLE[i];
      if (!other?.id || other.id === targetId) continue;

      const hasReverseRelationship = normalizeRelationships(
        other.relationships
      ).some((rel) =>
        matchesTargetRef(rel?.to, targetId, targetName, targetAliases)
      );

      if (hasReverseRelationship) {
        addUniquePerson(results, seen, other, targetId);
      }
    }

    return results;
  }

  function getExpandedPerson(personId) {
    const person = getPersonById(personId);
    if (!person) return null;

    return {
      ...person,
      expandedRelatedPeople: getRelatedPeopleForPerson(personId),
    };
  }

  return {
    normalizeName,
    getPersonById,
    getPeopleSuggestions,
    findPersonMatch,
    findPersonMatchWithScore,
    getRelatedPeopleForPerson,
    getExpandedPerson,
  };
}